import globalMaterialUITheme from '@alo/yoga-ui';
import { Grid, ThemeProvider } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useWindowSize } from 'src/scripts/templates/_shared/react-hooks';
import { IS_iOS, isSiteInEnglish } from 'src/scripts/utils/helpers';
import PopularProducts from './PopularProducts';
import RecentSearchList from './RecentSearch';
import SearchResultList from './SearchResultList';
import TrendingProductsList from './TrendingProducts';
import { ApolloProvider, useQuery } from '@apollo/client';
import { ProductNode } from 'src/scripts/templates/product-listing-page/types';
import { searchRespProductServiceProductToProductNode } from '../utils';
import client from '../../../../templates/_shared/apolloClient';
import { useDebounce } from 'usehooks-ts';
import { loader } from 'graphql.macro';
import {
  SearchService,
  SearchSuggestionsQuery,
  SearchSuggestionsQueryVariables,
} from 'src/generated/product-service-graphql';
import { getBrowseApiHeaders } from 'src/scripts/templates/search-page/SearchPageUtils/constants';

const GetSuggestionsGQL = loader('./../GetSearchSuggestions.product-service.graphql');
const NUMBER_OF_SEARCH_QUERY_SUGGESTIONS = 5;
const NUMBER_OF_SEARCH_PRODUCT_SUGGESTIONS = 4;

const SearchResultsModal = ({ searchText, isMobileVersion }: { searchText: string; isMobileVersion: boolean }) => {
  const [productItems, setProductsItems] = useState<ProductNode[]>([]);
  const [productSuggestions, setProductSuggestions] = useState<string[]>();
  const debouncedSearchText = useDebounce<string>(searchText, 300);
  const [showNoResults, setShowNoResults] = useState(false);
  const isEnglishUser = isSiteInEnglish();

  const { data, loading } = useQuery<SearchSuggestionsQuery, SearchSuggestionsQueryVariables>(GetSuggestionsGQL, {
    variables: {
      query: debouncedSearchText,
      searchService: SearchService.Bloomreach,
      querySuggestionSize: NUMBER_OF_SEARCH_QUERY_SUGGESTIONS,
      productSize: NUMBER_OF_SEARCH_PRODUCT_SUGGESTIONS,
    },
    skip: !debouncedSearchText,
    context: {
      headers: getBrowseApiHeaders(),
    },
    onError: (e) => {
      console.error(e);
    },
  });

  useEffect(() => {
    if (!isEnglishUser || !debouncedSearchText) {
      setProductsItems([]);
      setProductSuggestions([]);
      setShowNoResults(false);
      return;
    }

    if (data) {
      const psProducts = data.suggest?.products || [];
      const psQuerySuggestions = data.suggest?.querySuggestions || [];
      setProductsItems(psProducts.map((item) => searchRespProductServiceProductToProductNode(item)));
      setProductSuggestions(psQuerySuggestions);
      setShowNoResults(psProducts.length === 0);
    }
  }, [data, debouncedSearchText, isEnglishUser]);

  // space to render if on iOS to allow user to scroll all the way down in mobile search
  const spaceForScrollElement = <div style={{ height: Math.floor(useWindowSize().height * 0.2), width: '100%' }}></div>;

  const NoResultsSection = ({ searchText }: { searchText: string }) => {
    return (
      <div id="no-results-section" className="no-results-section d-flex flex-column">
        <h6 className="header-title h6">No Results Found</h6>
        <p className="no-results-text">
          Sorry, we couldn't find "<span className="query">{searchText}</span>"
        </p>
      </div>
    );
  };

  return (
    <ThemeProvider theme={globalMaterialUITheme}>
      <Grid
        container
        className={`search-result-block d-md-flex justify-content-center ${
          isMobileVersion ? 'search-result-block--mobile' : ''
        }`}
      >
        <Grid container id="search-results-section" className="search-results-section">
          <Grid
            id="searched-products-list"
            item
            container
            sm={12}
            md={12}
            lg={3}
            xl={3}
            direction="row"
            className="section-gap"
          >
            {/* no results placement for mobile */}
            {isMobileVersion && debouncedSearchText && !loading && showNoResults && (
              <NoResultsSection searchText={searchText} />
            )}
            {searchText && productItems.length > 0 && isEnglishUser ? (
              <SearchResultList
                searchText={searchText}
                productSuggestions={productSuggestions}
                isMobileVersion={isMobileVersion}
              />
            ) : (
              <>
                <RecentSearchList />
                <TrendingProductsList />
              </>
            )}
          </Grid>
          <Grid className="section-gap" item container sm={12} md={12} lg={8} xl={8}>
            <ApolloProvider client={client}>
              {/* no results placement for desktop */}
              {!isMobileVersion && debouncedSearchText && !loading && showNoResults && (
                <NoResultsSection searchText={searchText} />
              )}
              <PopularProducts
                productItems={productItems}
                searchText={searchText}
                isMobileVersion={isMobileVersion}
                isEnglishUser={isEnglishUser}
              ></PopularProducts>
            </ApolloProvider>
          </Grid>
          {IS_iOS ? spaceForScrollElement : null}
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};

export default SearchResultsModal;
